import {
  type Birthday,
  addDays,
  shortDate,
  todayIso,
  useBirthdays,
  useClasses,
  useEvents,
  useMenu,
} from '@shkola/api';
import { Link } from '@tanstack/react-router';
import { Utensils } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { EventArt, GRAD, Head, INK, INK_SOFT, LINE, grad, ringSmall } from '../design/forms';
import { IconTile } from '../design/ui';
import { ArrowUpRightIcon, CakeIcon } from '../design/icons';
import { useEventWhen } from '../pages/EventsPage';
import { EVENT_GRAD } from '../student/home/ShapesDay';

/*
 * «Життя школи» for a home in «Форми 2» (28.10): a white block with what is today in the canteen, the next
 * events (each with its kind's small picture) and the birthdays of the week in my classes; a row into
 * its section ends with the ↗ circle. The birthdays are also a block of their own for «Шкільне життя».
 */

/** «сьогодні», «завтра», «пт, 23 жовт.» */
export function useBirthdayWhen() {
  const { t } = useTranslation();
  const today = todayIso();
  return (b: Birthday) =>
    b.onDate === today
      ? t('studentHome.glass.school.today')
      : b.onDate === addDays(today, 1)
        ? t('birthdays.tomorrow')
        : shortDate(b.onDate);
}

const ROW = 'group flex items-center gap-3.5 py-3';

/** The ↗ at the end of a row into a section. */
function Arrow() {
  return (
    <span className={`${ringSmall} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}>
      <ArrowUpRightIcon size={14} weight="bold" aria-hidden />
    </span>
  );
}

/** The birthdays of the week in my classes: a cake in a pink circle, the name, the class and when. */
function BirthdayRows({
  list,
  classLabel,
}: {
  list: Birthday[];
  classLabel: (id: string) => string;
}) {
  const when = useBirthdayWhen();
  const today = todayIso();
  return (
    <ul className="flex flex-col">
      {list.map((b) => (
        <li
          key={b.memberId}
          className="flex items-center gap-3.5 border-t border-[#E4E0D7] py-3 first:border-t-0"
        >
          <span
            className="flex size-10 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundImage: grad('pink'), color: GRAD.pink.ink }}
          >
            <CakeIcon size={18} weight="fill" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[15px] leading-5" style={{ color: INK }}>
              {b.name}
            </span>
            <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
              {classLabel(b.classId)}
            </span>
          </span>
          <span
            className="shrink-0 rounded-[7px] px-2 py-1 text-[12px] leading-none font-medium"
            style={
              b.onDate === today
                ? { backgroundImage: grad('pink'), color: GRAD.pink.ink }
                : { color: INK_SOFT }
            }
          >
            {when(b)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** «Дні народження» as a block of its own; nothing when there are none this week. */
export function BirthdaysBlock({
  schoolId,
  className = '',
}: {
  schoolId: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const birthdays = useBirthdays(schoolId, 7);
  const classes = useClasses(schoolId);
  const list = birthdays.data ?? [];
  if (list.length === 0) return null;
  const classLabel = (id: string) => classes.data?.find((c) => c.id === id)?.label ?? '';
  return (
    <section className={`flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6 ${className}`}>
      <Head title={t('birthdays.title')} count={list.length} />
      <BirthdayRows list={list} classLabel={classLabel} />
    </section>
  );
}

/** Home page: today's lunch, the next events and the birthdays of the week in my classes. */
export function SchoolLifePanel({ schoolId }: { schoolId: string }) {
  const { t } = useTranslation();
  const today = todayIso();
  const menu = useMenu(schoolId, today, today);
  const events = useEvents(schoolId, today);
  const birthdays = useBirthdays(schoolId, 7);
  const classes = useClasses(schoolId);
  const when = useEventWhen();
  const lunch = (menu.data ?? []).filter((i) => i.meal === 'lunch');
  const next = (events.data ?? []).slice(0, 3);
  const classLabel = (id: string) => classes.data?.find((c) => c.id === id)?.label ?? '';
  const born = birthdays.data ?? [];

  if (lunch.length === 0 && next.length === 0 && born.length === 0) return null;
  return (
    <section className="flex min-w-0 flex-col rounded-[28px] bg-white p-5 md:p-6">
      <Head title={t('nav.schoolLife')} />
      <ul className="flex flex-col" style={{ borderTop: `1px solid ${LINE}` }}>
        {lunch.length > 0 && (
          <li style={{ borderBottom: `1px solid ${LINE}` }}>
            <Link to="/canteen" className={ROW}>
              <IconTile tone="yellow" icon={Utensils} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] leading-5 font-medium" style={{ color: INK }}>
                  {t('canteen.today')}
                </span>
                <span className="block text-[13px] leading-[18px]" style={{ color: INK_SOFT }}>
                  {lunch.map((i) => i.title).join(' · ')}
                </span>
              </span>
              <Arrow />
            </Link>
          </li>
        )}
        {next.map((e) => (
          <li key={e.id} style={{ borderBottom: `1px solid ${LINE}` }}>
            <Link to="/events" className={ROW}>
              <EventArt kind={e.kind} tone={EVENT_GRAD[e.kind]} size={40} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[15px] leading-5" style={{ color: INK }}>
                  {e.title}
                </span>
                <span
                  className="block truncate text-[13px] leading-[18px]"
                  style={{ color: INK_SOFT }}
                >
                  {when(e)}
                </span>
              </span>
              <Arrow />
            </Link>
          </li>
        ))}
      </ul>
      {born.length > 0 && (
        <div className="mt-5">
          <p className="mb-1 text-[15px] font-medium" style={{ color: INK }}>
            {t('birthdays.title')}
          </p>
          <BirthdayRows list={born} classLabel={classLabel} />
        </div>
      )}
    </section>
  );
}
